import i18nFactory from '@yandex-int/i18n';
import { FetchResult } from 'apollo-boost';
import { ApolloQueryResult } from 'apollo-client';
import { compact, findIndex, isEqual, some, uniq, values } from 'lodash';
import { Action } from 'redux';
import { Epic, ofType } from 'redux-observable';
import { EMPTY, from, Observable, of } from 'rxjs';
import { catchError, mergeMap, switchMap } from 'rxjs/operators';

import {
    ADD_END_DATE_FOR_EDIT_CAMPAIGNS,
    AddEndDateForEditCampaigns,
    CHANGE_SITE_MONITORING_FLAG_FOR_EDIT_CAMPAIGNS,
    changeCampaignsEditPageStatus,
    changeNotificationsDisableValueForEditCampaigns,
    ChangeSiteMonitoringFlagForEditCampaigns,
    DELETE_END_DATE_FOR_EDIT_CAMPAIGNS,
    DeleteEndDateForEditCampaigns,
    FETCH_CAMPAIGNS_GOALS_AVAILABLE_FOR_KEY_GOALS,
    fetchCampaignsGoalsAvailableForKeyGoals,
    FetchCampaignsGoalsAvailableForKeyGoals,
    GET_CAMPAIGNS_FOR_EDIT,
    GET_RETARGETING_CONDITIONS_FOR_CAMPAIGNS_EDIT,
    GetRetargetingConditionsForCampaignsEditAction,
    LEAVE_CAMPAIGNS_EDIT,
    leaveCampaignsEdit,
    LeaveCampaignsEditAction,
    ON_DONE_CAMPAIGN_ALLOWED_PAGE_IDS_EDITOR,
    ON_DONE_CAMPAIGN_BANNED_DOMAINS_EDITOR,
    ON_DONE_CAMPAIGN_DISABLED_IPS_EDITOR,
    ON_DONE_CAMPAIGN_RELEVANT_PHRASES_EDITOR,
    OnDoneCampaignAllowedPageIdsEditorAction,
    OnDoneCampaignBannedDomainsEditorAction,
    OnDoneCampaignDisabledIpsEditor,
    OnDoneCampaignRelevantPhrasesEditor,
    SAVE_CAMPAIGN_CHAT_LIST_EDITOR,
    SAVE_CAMPAIGN_EXPERIMENTS_EDITOR,
    SAVE_CAMPAIGN_METRIKA_COUNTER_EDITOR,
    SAVE_CAMPAIGN_METRIKA_KEY_GOALS_EDITOR,
    SAVE_CAMPAIGN_MINUS_PHRASES_EDITOR,
    SAVE_CAMPAIGN_NAME_EDITOR_CHANGES,
    SAVE_CAMPAIGN_RATE_CORRECTIONS_EDITOR,
    SAVE_CAMPAIGNS,
    SAVE_NOTIFICATIONS_EDITOR_FOR_EDIT_CAMPAIGNS,
    SAVE_TIME_TARGETING_EDITOR_FOR_EDIT_CAMPAIGNS,
    SAVE_VISITING_CARD_FOR_EDIT_CAMPAIGN,
    SaveCampaignChatListEditorAction,
    SaveCampaignExperimentsEditorAction,
    SaveCampaignMetrikaCounterEditorAction,
    SaveCampaignMetrikaKeyGoalsEditorAction,
    SaveCampaignMinusPhrasesEditor,
    SaveCampaignNameEditorChangesAction,
    SaveCampaignRateCorrectionsEditorAction,
    SaveNotificationsEditorForEditCampaigns,
    SaveTimeTargetingEditorForEditCampaigns,
    SaveVisitingCardForEditCampaign,
    showCampaignEditGeneralErrors,
    showCampaignEditGlobalError,
    showCampaignEditItemErrors,
    synchronizeVisitingCardForEditCampaigns,
    toggleCampaignsOtherSettingsVisibilityAction,
    UPDATE_CAMPAIGN_METRIKA_COUNTERS,
    updateCampaignAllowedPageIdsValue,
    updateCampaignAvailableForKeyGoals,
    updateCampaignBannedDomainsValue,
    updateCampaignChat,
    updateCampaignDisabledIpsValue,
    updateCampaignExperiments,
    UpdateCampaignMetrikaCounters,
    updateCampaignMetrikaCounters,
    updateCampaignMetrikaKeyGoals,
    updateCampaignMinusPhrases,
    updateCampaignName,
    updateCampaignNotifications,
    updateCampaignRateCorrections,
    updateCampaignRelevantPhrases,
    updateCampaignStrategy,
    updateCampaignTimeTargetingForEditCampaigns,
    updateDataForEditCampaigns,
    updateRetargetingConditionsForCampaignsEdit
} from '../../actions/campaignsEdit';
import {
    closeChatListEditor,
    fetchChatsChatListEditor
} from '../../actions/chatList';
import { changeLocation, updateMetroStations } from '../../actions/common';
import { updateDatesIntervalEditors } from '../../actions/datesIntervalEditor';
import {
    closeEditPageItemNameEditor,
    showEditPageItemNameEditorErrors
} from '../../actions/editPageItemNameEditor';
import { closeExperimentsEditor } from '../../actions/experimentsEditor';
import {
    closeItemsListTextareaEditor,
    updateItemsListEditorTextAreaErrors
} from '../../actions/itemsListTextareaEditor';
import {
    closeMetrikaCounterEditor,
    fetchCountersMetrikaCounterEditor,
    showMetrikaCounterEditorErrors
} from '../../actions/metrikaCounterEditor';
import {
    closeMetrikaKeyGoalsEditor,
    initMetrikaKeyGoalsEditors,
    showMetrikaKeyGoalsEditorErrors,
    showMetrikaKeyGoalsEditorMetrikaIsUnavailable
} from '../../actions/metrikaKeyGoals';
import { closeMinusPhrasesEditor } from '../../actions/minusPhrasesEditor';
import {
    changeNotificationDisableValueForNotificationsEditor,
    closeNotificationsEditor,
    updateNotificationsEditorValidationData
} from '../../actions/notificationsEditor';
import { updateRateCorrectionsEditorValidation } from '../../actions/rateCorrectionsEditor';
import {
    closeCampaignRelevantPhrasesEditor,
    initCampaignsRelevantPhrasesEditors,
    showRelevantPhrasesEditorErrors
} from '../../actions/relevantPhrases';
import {
    applyStrategyEditorValidationResult,
    closeStrategyEditor,
    initStrategyEditors,
    StrategyEditorActionType,
    ValidateAndAttemptSaveAction
} from '../../actions/strategyEditor';
import {
    closeTimeTargetingEditor,
    updateValidationDataForTimeTargetingEditor
} from '../../actions/timeTargetingEditor';
import {
    closeVCardEditor,
    updateVisitingCardsErrors
} from '../../actions/visitingCardEditor';
import { FetchMinusPhraseLibraryDataResult } from '../../api/MinusPhraseLibraryProvider/MinusPhraseLibraryProvider';
import { mapMetroStationsFromApi } from '../../mappers/banners-edit/api-data-mappers';
import {
    getNotificationsValueFromNotificationsEditor,
    getTimeTargetingValueFromTimeTargetingEditor,
    mapCampaignsDataForEditToInitKeyGoalsData,
    mapCampaignsDataForEditToInitRelevantPhrasesEditors,
    mapCampaignsDataToApiFormat,
    mapCampaignsEditServerValidationResultToEditorsValidationResult,
    mapCampaignsForEditQueryResultToQueryResponse,
    mapCampaignsToFetchAvailableGoalsForKeyInput,
    mapDatesIntervalEditorsDataFromCampaigns,
    mapMeaningfulGoalsQueryResultToGoalsAvailableForKeyGoals,
    mapRetargetingConditionForCampaignsEditFromApiFormat
} from '../../mappers/campaignsEdit/campaignsEdit';
import { mapChatListEditorValueToCampaignChatValue } from '../../mappers/chatListEditor/chatListEditor';
import { mapEditorExperimentsToCampaignExperiments } from '../../mappers/experimentsEditor';
import { mapMetrikaCountersEditorValueToCampaignMetrikaCountersValue } from '../../mappers/metrikaCounters';
import {
    mapFetchResultToLibraryItem,
    mapMinusPhrasesEditorValueToCampaignMinusPhrasesValue
} from '../../mappers/minusPhrases';
import { mapEditorRateCorrectionsToRateCorrections } from '../../mappers/rateCorrections/rateCorrections';
import { mapRelevantPhrasesEditorValueToCampaignRelevantPhrasesValue } from '../../mappers/relevantPhrases';
import {
    mapCampaignsDataForEditToInitStrategyData,
    mapStrategyForCampaignData
} from '../../mappers/strategyEditor';
import { awaitCommonData } from '../../operators';
import {
    getCampaignData,
    getCampaignValue,
    selectCampaignNotificationsValue
} from '../../selectors/campaignsEdit/campaignsEdit';
import { selectEditPageItemNameEditorValue } from '../../selectors/editPageItemNameEditor/editPageItemNameEditor';
import { selectClientFeatures } from '../../selectors/groupEdit/group/group';
import { selectNotificationsEditorValue } from '../../selectors/notificationsEditor/notificationsEditor';
import { selectRelevantPhrasesEditorValue } from '../../selectors/relevantPhrasesEditor/relevantPhrasesEditor';
import { selectTimeTargetingEditorValue } from '../../selectors/timeTargetingEditor/timeTargetingEditor';
import { createCampaignsEditErrorMapper } from '../../service/CampaignsEditHelpers/CampaignsEditBackErrorMapper/CampaignsEditBackErrorMapper';
import {
    getCampaignAllowedPageIdsEditorId,
    getCampaignBannedDomainsEditorId,
    getCampaignChatListEditorId,
    getCampaignDisabledIpsEditorId,
    getCampaignEditPageEditorId,
    getCampaignEditPageItemEditorId,
    getCampaignExperimentsEditorId,
    getCampaignMetrikaKeyGoalsEditorId,
    getCampaignMinusPhrasesEditorId,
    getCampaignsDataWithOpenedEditors,
    getCampaignStrategyEditorId,
    validateCampaignAllowedPageIdsEditor,
    validateCampaignBannedDomainsEditor,
    validateCampaignDisabledIpsEditor,
    validateCampaignMetrikaCountersEditor,
    validateCampaignNameEditor,
    validateCampaignRateCorrectionsEditor,
    validateCampaignRelevantPhrasesEditor,
    validateCampaignsEditEditors,
    validateMetrikaCampaignKeyGoalsEditor
} from '../../service/CampaignsEditHelpers/CampaignsEditHelpers';
import { confirm } from '../../service/Confirm/Confirm';
import { ApiMethodExecutingError, NotSyncedError } from '../../service/Errors';
import { validateNotificationsEditor } from '../../service/NotificationEditorHelper/NotificationEditorHelper';
import { EpicDeps } from '../../service/StoreFactory';
import {
    isStrategyValidationResultPositive,
    validateStrategy
} from '../../service/StrategyEditorValidators/StrategyEditorValidators';
import { UrlHelpers } from '../../service/UrlHelpers';
import {
    CampaignsEditEditorsValidationResult,
    DataForEditCampaigns,
    EditCampaignEditor,
    EditCampaignsErrorsData,
    EditCampaignValueField
} from '../../typings/campaignsEdit';
import { PageStatus } from '../../typings/common';
import { DnaEpic, DnaEpicAction } from '../../typings/epic';
import {
    RetargetingConditionsQueryForCampaignsEdit,
    UpdateCampaigns
} from '../../typings/graphql-grid';
import { NotificationType } from '../../typings/notificationEditor';
import { State } from '../../typings/state';

import * as keyset from './campaignsEdit.i18n';

const i18n = i18nFactory(keyset);

export const initCampaignsEditors = (
    campaignsData: DataForEditCampaigns
): Action[] => {
    const datesIntervalEditorsData = mapDatesIntervalEditorsDataFromCampaigns(
        campaignsData
    );
    const hasUpdatesEditorsForDatesIntervalEditors =
        Object.keys(datesIntervalEditorsData).length > 0;

    // в этот массив можно добавлять новые экшны инициализации редакторов страницы кампаний (должны зависеть от enabledFields)
    // сейчас редакторы обычно инициализируются на их раскрытие, поэтому сюда пока можно добавлять только редакторы,
    // которым необходима инициализация сразу при загрузки страницы с описанием причины в комментариях
    return compact([
        // редактор дат должен быть проинициализирован сразу, так как он сразу открыт
        hasUpdatesEditorsForDatesIntervalEditors &&
            updateDatesIntervalEditors(datesIntervalEditorsData),
        // необходимо инициализировать данными,
        // так как могут прийти невалидные данные от бэка и нам надо сразу показать ошибку
        initStrategyEditors(
            mapCampaignsDataForEditToInitStrategyData(campaignsData.campaigns)
        ),
        // необходимо инициализировать данными,
        // так как могут прийти невалидные данные от бэка и нам надо сразу показать ошибку
        initCampaignsRelevantPhrasesEditors(
            mapCampaignsDataForEditToInitRelevantPhrasesEditors(
                campaignsData.campaigns
            )
        ),
        // необходимо инициализировать данными,
        // так как могут прийти невалидные данные от бэка и нам надо сразу показать ошибку
        initMetrikaKeyGoalsEditors(
            mapCampaignsDataForEditToInitKeyGoalsData(campaignsData.campaigns)
        )
    ]);
};

export const getCampaignsForEditEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(GET_CAMPAIGNS_FOR_EDIT),
        awaitCommonData(action$, store$),
        mergeMap(
            (): Observable<Action> => {
                const state = store$.value;
                const { login, constants } = state.commonData;
                if (constants === undefined) {
                    throw new Error('отсутсвует поле constants в commonData');
                }
                const { campaignsIds } = state.editCampaigns;

                if (campaignsIds.length === 0) {
                    const url = UrlHelpers.getCampaignListUrl();
                    return of(leaveCampaignsEdit(url, false));
                }

                return from(
                    deps.campaignsDataProvider.getCampaigns(login, campaignsIds)
                ).pipe(
                    mergeMap(
                        (campaignsResult): Observable<Action> => {
                            const metroStations = mapMetroStationsFromApi(
                                campaignsResult.data.constants.metroStations
                            );
                            const campaignsForEditData = mapCampaignsForEditQueryResultToQueryResponse(
                                campaignsResult,
                                constants,
                                metroStations,
                                selectClientFeatures(state)
                            );

                            return of(
                                ...initCampaignsEditors(campaignsForEditData),
                                updateDataForEditCampaigns(
                                    campaignsForEditData
                                ),
                                fetchCountersMetrikaCounterEditor(),
                                fetchCampaignsGoalsAvailableForKeyGoals(
                                    mapCampaignsToFetchAvailableGoalsForKeyInput(
                                        campaignsForEditData.campaigns
                                    )
                                ),
                                updateMetroStations(metroStations),
                                fetchChatsChatListEditor()
                            );
                        }
                    ),
                    catchError(
                        (
                            err: Error | ApiMethodExecutingError
                        ): Observable<Action> => {
                            const message =
                                err instanceof ApiMethodExecutingError
                                    ? err.getMessage()
                                    : err.message;

                            return of(
                                showCampaignEditGlobalError({
                                    title: i18n('page-common-error'),
                                    hint:
                                        message ||
                                        i18n('page-common-error-hint')
                                })
                            );
                        }
                    )
                );
            }
        )
    );
};

export const fetchCampaignsGoalsAvailableForKeyGoalsEpic: DnaEpic = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(FETCH_CAMPAIGNS_GOALS_AVAILABLE_FOR_KEY_GOALS),
        mergeMap(
            (
                action: FetchCampaignsGoalsAvailableForKeyGoals
            ): DnaEpicAction => {
                const state = store$.value;
                const { fetchAvailableForKeyGoalsInput } = action;
                const {
                    commonData: { login }
                } = state;

                return from(
                    deps.campaignsDataProvider.getGoalsAvailableForKeyGoals(
                        login,
                        fetchAvailableForKeyGoalsInput
                    )
                ).pipe(
                    mergeMap(
                        (result): DnaEpicAction => {
                            const {
                                campaignGoals: apiCampaignGoals,
                                isMetrikaAvailable
                            } = result.data.client.meaningfulGoals;

                            if (!isMetrikaAvailable) {
                                throw new Error('Метрика недоступна');
                            }

                            const campaignGoals = mapMeaningfulGoalsQueryResultToGoalsAvailableForKeyGoals(
                                apiCampaignGoals
                            );

                            return [
                                updateCampaignAvailableForKeyGoals(
                                    isMetrikaAvailable,
                                    campaignGoals
                                )
                            ];
                        }
                    ),
                    catchError(() => {
                        return Object.keys(state.metrikaKeyGoalsEditor).map(
                            editorId => {
                                return showMetrikaKeyGoalsEditorMetrikaIsUnavailable(
                                    editorId
                                );
                            }
                        );
                    })
                );
            }
        )
    );
};

export const saveCampaignChatEpic: DnaEpic = (action$, store, deps) => {
    return action$.pipe(
        ofType(SAVE_CAMPAIGN_CHAT_LIST_EDITOR),
        switchMap(
            (action: SaveCampaignChatListEditorAction): DnaEpicAction => {
                const { campaignId } = action;
                const {
                    chatListEditor: { editors }
                } = store.value;
                const editorId = getCampaignChatListEditorId(campaignId);

                return [
                    updateCampaignChat(
                        campaignId,
                        mapChatListEditorValueToCampaignChatValue(
                            editors[editorId]
                        )
                    ),
                    closeChatListEditor(editorId)
                ];
            }
        )
    );
};

export const getCampaignsEditEditorsShowErrorsActions = (
    errorsData: CampaignsEditEditorsValidationResult[]
): Action[] => {
    return errorsData.reduce(
        (res: Action[], errorData: CampaignsEditEditorsValidationResult) => {
            let actions: Action[] = [];
            const { campaignId } = errorData;
            const editorId = getCampaignEditPageItemEditorId(campaignId);

            switch (errorData.editor) {
                case EditCampaignEditor.nameEditor:
                    actions = [
                        showEditPageItemNameEditorErrors(
                            editorId,
                            errorData.errors
                        )
                    ];
                    break;

                case EditCampaignEditor.disabledIpsEditor:
                    actions = [
                        updateItemsListEditorTextAreaErrors(
                            editorId,
                            errorData.errors
                        )
                    ];
                    break;

                case EditCampaignEditor.bannedDomainsEditor:
                    actions = [
                        updateItemsListEditorTextAreaErrors(
                            getCampaignBannedDomainsEditorId(campaignId),
                            errorData.errors
                        )
                    ];
                    break;

                case EditCampaignEditor.allowedPageIdsEditor:
                    actions = [
                        updateItemsListEditorTextAreaErrors(
                            getCampaignAllowedPageIdsEditorId(campaignId),
                            errorData.errors
                        )
                    ];
                    break;

                case EditCampaignEditor.relevantPhrasesEditor:
                    actions = [
                        showRelevantPhrasesEditorErrors(
                            editorId,
                            errorData.errors
                        )
                    ];
                    break;

                case EditCampaignEditor.notificationsEditor:
                    actions = [
                        updateNotificationsEditorValidationData(
                            editorId,
                            errorData.errors
                        )
                    ];
                    break;

                case EditCampaignEditor.rateCorrectionsEditor:
                    actions = [
                        updateRateCorrectionsEditorValidation(
                            editorId,
                            errorData.errors
                        )
                    ];
                    break;

                case EditCampaignEditor.strategyEditor:
                    actions = [
                        applyStrategyEditorValidationResult(
                            getCampaignStrategyEditorId(campaignId),
                            errorData.errors
                        )
                    ];
                    break;

                case EditCampaignEditor.metrikaCounterEditor:
                    actions = [
                        showMetrikaCounterEditorErrors(
                            getCampaignEditPageItemEditorId(campaignId),
                            errorData.errors
                        )
                    ];
                    break;

                case EditCampaignEditor.metrikaKeyGoalsEditor:
                    actions = [
                        showMetrikaKeyGoalsEditorErrors(
                            getCampaignMetrikaKeyGoalsEditorId(campaignId),
                            errorData.keyGoalsValidationData
                        )
                    ];
                    break;

                case EditCampaignEditor.bannersLanguage:
                    actions = [
                        showCampaignEditItemErrors(
                            campaignId,
                            EditCampaignValueField.bannersLanguage,
                            errorData.errors
                        )
                    ];
                    break;

                case EditCampaignEditor.visitingCardEditor:
                    actions = [
                        updateVisitingCardsErrors(
                            getCampaignEditPageItemEditorId(campaignId),
                            errorData.errors
                        )
                    ];
                    break;

                case 'general':
                    actions = [
                        showCampaignEditItemErrors(
                            campaignId,
                            'general',
                            errorData.errors
                        )
                    ];
                    break;

                default:
                    break;
            }

            return [...res, ...actions];
        },
        []
    );
};

export const saveCampaignsEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(SAVE_CAMPAIGNS),
        mergeMap(
            (): Observable<Action> => {
                const state = store$.value;

                const editorsErrors = validateCampaignsEditEditors(state);
                const {
                    commonData: { login },
                    editCampaigns: { campaignsIds, constants }
                } = state;

                if (constants === undefined) {
                    throw Error();
                }

                if (editorsErrors.length !== 0) {
                    return from([
                        changeCampaignsEditPageStatus(PageStatus.LOADED),
                        ...getCampaignsEditEditorsShowErrorsActions(
                            editorsErrors
                        )
                    ]);
                }

                const campaignsValue = getCampaignsDataWithOpenedEditors(state);

                return from(
                    deps.campaignsMutationExecutor.updateCampaigns(
                        mapCampaignsDataToApiFormat(campaignsValue, constants),
                        login
                    )
                ).pipe(
                    switchMap((response: FetchResult<UpdateCampaigns>) => {
                        const responseData = response.data;

                        if (
                            responseData === undefined ||
                            responseData.updateCampaigns === undefined
                        ) {
                            throw Error();
                        }

                        const updateCampaignsServerErrors =
                            (responseData.updateCampaigns.validationResult &&
                                responseData.updateCampaigns.validationResult
                                    .errors) ||
                            [];

                        if (updateCampaignsServerErrors.length !== 0) {
                            const updateCampaignsErrorMapper = createCampaignsEditErrorMapper(
                                campaignsIds
                            );

                            const updateCampaignsErrorsData = updateCampaignsErrorMapper.mapServerErrorsToClientError(
                                updateCampaignsServerErrors
                            ) as EditCampaignsErrorsData;

                            const editorsErrorsData = mapCampaignsEditServerValidationResultToEditorsValidationResult(
                                updateCampaignsErrorsData
                            );

                            return from([
                                changeCampaignsEditPageStatus(
                                    PageStatus.LOADED
                                ),
                                toggleCampaignsOtherSettingsVisibilityAction(
                                    uniq(
                                        editorsErrorsData.map(
                                            editorErrorData =>
                                                editorErrorData.campaignId
                                        )
                                    )
                                ),
                                ...getCampaignsEditEditorsShowErrorsActions(
                                    editorsErrorsData
                                )
                            ]);
                        }

                        const url = UrlHelpers.getCampaignListUrl();

                        return of(leaveCampaignsEdit(url, false));
                    }),
                    catchError(
                        (
                            error:
                                | Error
                                | ApiMethodExecutingError
                                | NotSyncedError<UpdateCampaigns>
                        ) => {
                            if (error instanceof NotSyncedError) {
                                return [
                                    changeCampaignsEditPageStatus(
                                        PageStatus.LOADED
                                    )
                                ];
                            }

                            const message =
                                error instanceof ApiMethodExecutingError
                                    ? error.getMessage()
                                    : error.message ||
                                      i18n('save-campaigns-error');

                            return [
                                showCampaignEditGeneralErrors([
                                    {
                                        text: message
                                    }
                                ]),
                                changeCampaignsEditPageStatus(PageStatus.LOADED)
                            ];
                        }
                    )
                );
            }
        )
    );
};

export const leaveCampaignsEditEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofType(LEAVE_CAMPAIGNS_EDIT),
        switchMap(
            (action: LeaveCampaignsEditAction): Observable<Action> => {
                const state = store$.value;
                const campaignsValue = getCampaignsDataWithOpenedEditors(state);

                const wasDataChanged = some(campaignsValue, campaign => {
                    return !isEqual(campaign.value, campaign.serverValue);
                });
                const { location } = action;
                const locationParams =
                    typeof location === 'string'
                        ? {
                              pathName: location,
                              query: { ulogin: state.commonData.login }
                          }
                        : {
                              pathName: location.pathname,
                              query: location.query
                          };

                if (wasDataChanged && action.showConfirm) {
                    return from(
                        confirm({
                            message: i18n('warning-about-changes')
                        })
                    ).pipe(
                        switchMap(() =>
                            of(
                                changeLocation(
                                    locationParams.pathName,
                                    locationParams.query
                                )
                            )
                        ),
                        catchError(() => EMPTY)
                    );
                }

                return of(
                    changeLocation(
                        locationParams.pathName,
                        locationParams.query
                    )
                );
            }
        )
    );
};

export const getRetargetingConditionsForCampaignsEditEpic: DnaEpic = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(GET_RETARGETING_CONDITIONS_FOR_CAMPAIGNS_EDIT),
        awaitCommonData(action$, store$),
        mergeMap(
            (
                action: GetRetargetingConditionsForCampaignsEditAction
            ): Observable<Action> => {
                return from(
                    deps.campaignsDataProvider.getRetargetingConditions(
                        action.login
                    )
                ).pipe(
                    mergeMap(
                        (
                            retargetingResult: ApolloQueryResult<
                                RetargetingConditionsQueryForCampaignsEdit
                            >
                        ): Observable<Action> => {
                            const retargetingConditions =
                                retargetingResult.data.client
                                    .retargetingConditions.rowset;

                            return of(
                                updateRetargetingConditionsForCampaignsEdit(
                                    retargetingConditions.map(
                                        mapRetargetingConditionForCampaignsEditFromApiFormat
                                    )
                                )
                            );
                        }
                    ),
                    catchError(
                        (): Observable<Action> => {
                            return of(
                                updateRetargetingConditionsForCampaignsEdit([])
                            );
                        }
                    )
                );
            }
        )
    );
};

export const saveCampaignRelevantPhrasesEpic: DnaEpic = (
    action$,
    store,
    deps
) => {
    return action$.pipe(
        ofType(ON_DONE_CAMPAIGN_RELEVANT_PHRASES_EDITOR),
        mergeMap(
            (
                action: OnDoneCampaignRelevantPhrasesEditor
            ): Observable<Action> => {
                const state = store.value;
                const { campaignId } = action;
                const editorId = getCampaignEditPageItemEditorId(campaignId);

                const {
                    isValid,
                    errors
                } = validateCampaignRelevantPhrasesEditor(campaignId, state);

                if (!isValid) {
                    return of(
                        showRelevantPhrasesEditorErrors(editorId, errors)
                    );
                }

                return of(
                    updateCampaignRelevantPhrases(
                        campaignId,
                        mapRelevantPhrasesEditorValueToCampaignRelevantPhrasesValue(
                            selectRelevantPhrasesEditorValue(state, editorId)
                        )
                    ),
                    closeCampaignRelevantPhrasesEditor(editorId)
                );
            }
        )
    );
};

export const saveCampaignMetrikaCountersEpic: DnaEpic = (
    action$,
    store,
    deps
) => {
    return action$.pipe(
        ofType(SAVE_CAMPAIGN_METRIKA_COUNTER_EDITOR),
        switchMap(
            (action: SaveCampaignMetrikaCounterEditorAction): Action[] => {
                const { campaignId } = action;
                const { metrikaCounterEditor } = store.value;
                const editorId = getCampaignEditPageItemEditorId(campaignId);

                const newCountersValue = mapMetrikaCountersEditorValueToCampaignMetrikaCountersValue(
                    metrikaCounterEditor.editors[editorId]
                );

                const errors = validateCampaignMetrikaCountersEditor(
                    campaignId,
                    store.value
                );

                if (errors.length !== 0) {
                    return [showMetrikaCounterEditorErrors(editorId, errors)];
                }

                return [
                    updateCampaignMetrikaCounters(campaignId, newCountersValue),
                    closeMetrikaCounterEditor(editorId)
                ];
            }
        )
    );
};

export const updateCampaignMetrikaCountersEpic: DnaEpic = (
    action$,
    store,
    deps
) => {
    return action$.pipe(
        ofType(UPDATE_CAMPAIGN_METRIKA_COUNTERS),
        switchMap(
            (action: UpdateCampaignMetrikaCounters): DnaEpicAction => {
                const {
                    campaignId,
                    metrikaCounters: { counters }
                } = action;

                return [
                    fetchCampaignsGoalsAvailableForKeyGoals([
                        {
                            campaignId,
                            counterIds: counters
                        }
                    ])
                ];
            }
        )
    );
};

export const updateCampaignNameEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(SAVE_CAMPAIGN_NAME_EDITOR_CHANGES),
        switchMap(
            (action: SaveCampaignNameEditorChangesAction): Action[] => {
                const { campaignId } = action;
                const state = store$.value;
                const { editPageItemNameEditor } = state;
                const editorId = getCampaignEditPageEditorId(campaignId);
                const nameEditorData = selectEditPageItemNameEditorValue(
                    editPageItemNameEditor,
                    editorId
                );

                const nameValidationErrors = validateCampaignNameEditor(
                    campaignId,
                    state
                );

                if (nameValidationErrors.length === 0) {
                    return [
                        updateCampaignName(campaignId, nameEditorData.value),
                        closeEditPageItemNameEditor(editorId)
                    ];
                }

                return [
                    showEditPageItemNameEditorErrors(
                        editorId,
                        nameValidationErrors
                    )
                ];
            }
        )
    );
};

export const saveCampaignNotificationEditorEpic: DnaEpic = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(SAVE_NOTIFICATIONS_EDITOR_FOR_EDIT_CAMPAIGNS),
        switchMap(
            (action: SaveNotificationsEditorForEditCampaigns): Action[] => {
                const state = store$.value;
                const {
                    commonData: { constants }
                } = state;
                const editorId = getCampaignEditPageEditorId(action.campaignId);
                const notificationEditorState = selectNotificationsEditorValue(
                    state,
                    editorId
                );

                const validationData = validateNotificationsEditor(
                    notificationEditorState,
                    constants
                );

                if (!validationData.isValid) {
                    return [
                        updateNotificationsEditorValidationData(
                            editorId,
                            validationData
                        )
                    ];
                }

                const campaignDataFromNotificationEditor = getNotificationsValueFromNotificationsEditor(
                    notificationEditorState
                );

                return [
                    updateCampaignNotifications(
                        action.campaignId,
                        campaignDataFromNotificationEditor
                    ),
                    closeNotificationsEditor(editorId)
                ];
            }
        )
    );
};

export const saveCampaignTimeTargetingEditorEpic: DnaEpic = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(SAVE_TIME_TARGETING_EDITOR_FOR_EDIT_CAMPAIGNS),
        switchMap(
            (action: SaveTimeTargetingEditorForEditCampaigns): Action[] => {
                const { campaignId } = action;
                const state = store$.value;
                const timeTargetingEditor = selectTimeTargetingEditorValue(
                    state,
                    campaignId
                );
                const campaignDataFromTimeTargetingEditor = getTimeTargetingValueFromTimeTargetingEditor(
                    timeTargetingEditor
                );

                const errorsValidation = [];

                if (errorsValidation.length !== 0) {
                    return [
                        updateValidationDataForTimeTargetingEditor(campaignId)
                    ];
                }

                return [
                    updateCampaignTimeTargetingForEditCampaigns(
                        campaignId,
                        campaignDataFromTimeTargetingEditor
                    ),
                    closeTimeTargetingEditor(campaignId)
                ];
            }
        )
    );
};

export const updateCampaignDisabledIpsEpic: DnaEpic = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(ON_DONE_CAMPAIGN_DISABLED_IPS_EDITOR),
        switchMap(
            (action: OnDoneCampaignDisabledIpsEditor): Action[] => {
                const { campaignId } = action;
                const state = store$.value;
                const editorId = getCampaignDisabledIpsEditorId(campaignId);

                const validationErrors = validateCampaignDisabledIpsEditor(
                    campaignId,
                    state
                );

                if (validationErrors.length === 0) {
                    return [
                        updateCampaignDisabledIpsValue(
                            campaignId,
                            action.value
                        ),
                        closeItemsListTextareaEditor(editorId)
                    ];
                }

                return [
                    updateItemsListEditorTextAreaErrors(
                        editorId,
                        validationErrors
                    )
                ];
            }
        )
    );
};

export const updateCampaignBannedDomainsEpic: DnaEpic = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(ON_DONE_CAMPAIGN_BANNED_DOMAINS_EDITOR),
        switchMap(
            (action: OnDoneCampaignBannedDomainsEditorAction): Action[] => {
                const { campaignId } = action;
                const state = store$.value;
                const editorId = getCampaignBannedDomainsEditorId(campaignId);

                const validationErrors = validateCampaignBannedDomainsEditor(
                    campaignId,
                    state
                );

                if (validationErrors.length !== 0) {
                    return [
                        updateItemsListEditorTextAreaErrors(
                            editorId,
                            validationErrors
                        )
                    ];
                }

                return [
                    updateCampaignBannedDomainsValue(campaignId, action.value),
                    closeItemsListTextareaEditor(editorId)
                ];
            }
        )
    );
};

export const updateCampaignAllowedPageIdsEpic: DnaEpic = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(ON_DONE_CAMPAIGN_ALLOWED_PAGE_IDS_EDITOR),
        switchMap(
            (action: OnDoneCampaignAllowedPageIdsEditorAction): Action[] => {
                const state = store$.value;
                const { campaignId } = action;
                const editorId = getCampaignAllowedPageIdsEditorId(campaignId);

                const validationErrors = validateCampaignAllowedPageIdsEditor(
                    campaignId,
                    state
                );

                if (validationErrors.length > 0) {
                    return [
                        updateItemsListEditorTextAreaErrors(
                            editorId,
                            validationErrors
                        )
                    ];
                } else {
                    return [
                        updateCampaignAllowedPageIdsValue(
                            campaignId,
                            action.value
                        ),
                        closeItemsListTextareaEditor(editorId)
                    ];
                }
            }
        )
    );
};

export const saveCampaignExperimentsEditorEpic: DnaEpic = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(SAVE_CAMPAIGN_EXPERIMENTS_EDITOR),
        mergeMap(
            (
                action: SaveCampaignExperimentsEditorAction
            ): Observable<Action> => {
                const { campaignId } = action;
                const editorId = getCampaignExperimentsEditorId(campaignId);
                const state = store$.value;
                const experimentsEditorState =
                    state.experimentsEditor[editorId];

                if (experimentsEditorState.areCorrectionsEnabled) {
                    const hasErrors = values(
                        experimentsEditorState.correctionsMap
                    ).some(correctionItem => {
                        return correctionItem.errors.length !== 0;
                    });

                    if (hasErrors) {
                        return EMPTY;
                    }
                }

                return from([
                    updateCampaignExperiments(
                        campaignId,
                        mapEditorExperimentsToCampaignExperiments(
                            experimentsEditorState
                        )
                    ),
                    closeExperimentsEditor(editorId)
                ]);
            }
        )
    );
};

export const saveCampaignMetrikaKeyGoalsEditorEpic: DnaEpic = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(SAVE_CAMPAIGN_METRIKA_KEY_GOALS_EDITOR),
        switchMap(
            (
                action: SaveCampaignMetrikaKeyGoalsEditorAction
            ): Action[] | Observable<Action> => {
                const state = store$.value;
                const { campaignId } = action;
                const editorId = getCampaignMetrikaKeyGoalsEditorId(campaignId);

                const {
                    isValid,
                    keyGoalsValidationData
                } = validateMetrikaCampaignKeyGoalsEditor(campaignId, state);

                if (isValid) {
                    return [
                        updateCampaignMetrikaKeyGoals(
                            campaignId,
                            state.metrikaKeyGoalsEditor[editorId].selectedGoals
                        ),
                        closeMetrikaKeyGoalsEditor(editorId)
                    ];
                }

                return [
                    showMetrikaKeyGoalsEditorErrors(
                        editorId,
                        keyGoalsValidationData
                    )
                ];
            }
        )
    );
};

export const saveCampaignRateCorrectionEditorEpic: DnaEpic = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(SAVE_CAMPAIGN_RATE_CORRECTIONS_EDITOR),
        switchMap((action: SaveCampaignRateCorrectionsEditorAction) => {
            const state = store$.value;
            const { editorId, campaignId } = action;
            const editorState = state.rateCorrectionsEditor[editorId].value;

            const validationResult = validateCampaignRateCorrectionsEditor(
                campaignId,
                state
            );

            if (validationResult.isValid) {
                return [
                    updateRateCorrectionsEditorValidation(
                        editorId,
                        validationResult.errors
                    ),
                    updateCampaignRateCorrections(
                        campaignId,
                        mapEditorRateCorrectionsToRateCorrections(editorState)
                    )
                ];
            } else {
                return [
                    updateRateCorrectionsEditorValidation(
                        editorId,
                        validationResult.errors
                    )
                ];
            }
        })
    );
};

export const addEndDateForEditCampaignEpic: DnaEpic = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(ADD_END_DATE_FOR_EDIT_CAMPAIGNS),
        switchMap((action: AddEndDateForEditCampaigns) => {
            const state = store$.value;
            const { campaignId } = action;

            return from(
                getActionsNotificationsDisableValueForNotificationsEditor(
                    state,
                    campaignId,
                    NotificationType.endCampaign,
                    false
                )
            );
        })
    );
};

export const deleteEndDateForEditCampaignEpic: DnaEpic = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(DELETE_END_DATE_FOR_EDIT_CAMPAIGNS),
        switchMap((action: DeleteEndDateForEditCampaigns) => {
            const state = store$.value;
            const { campaignId } = action;

            return from(
                getActionsNotificationsDisableValueForNotificationsEditor(
                    state,
                    campaignId,
                    NotificationType.endCampaign,
                    true
                )
            );
        })
    );
};

export const changeSiteMonitoringFlagForEditCampaignEpic: DnaEpic = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(CHANGE_SITE_MONITORING_FLAG_FOR_EDIT_CAMPAIGNS),
        switchMap((action: ChangeSiteMonitoringFlagForEditCampaigns) => {
            const state = store$.value;
            const { campaignId } = action;
            const campaignValue = getCampaignValue(state, campaignId);
            const { siteMonitoring } = campaignValue;

            return from(
                getActionsNotificationsDisableValueForNotificationsEditor(
                    state,
                    campaignId,
                    NotificationType.monitoringResults,
                    !siteMonitoring
                )
            );
        })
    );
};

export const getActionsNotificationsDisableValueForNotificationsEditor = (
    state: State,
    campaignId: string,
    notificationType: NotificationType,
    disableValue: boolean
) => {
    const editorId = getCampaignEditPageEditorId(campaignId);
    const { notificationEditor } = state;
    const currentNotificationEditor = notificationEditor[editorId];
    const notificationsValue = selectCampaignNotificationsValue(
        state,
        campaignId
    );
    const { notifications, phone } =
        currentNotificationEditor || notificationsValue;
    const indexEndCampaignNotification = findIndex(
        notifications,
        notification => notification.type === notificationType
    );

    const actionsList = [
        changeNotificationsDisableValueForEditCampaigns(
            campaignId,
            indexEndCampaignNotification,
            disableValue
        )
    ];

    if (currentNotificationEditor === undefined) {
        return actionsList;
    }

    if (indexEndCampaignNotification !== undefined && phone !== undefined) {
        return [
            changeNotificationDisableValueForNotificationsEditor(
                editorId,
                indexEndCampaignNotification,
                disableValue
            ),
            ...actionsList
        ];
    } else {
        return EMPTY;
    }
};

export const saveCampaignStrategyEditorEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofType(StrategyEditorActionType.VALIDATE_AND_ATTEMPT_SAVE),
        switchMap(
            (action: ValidateAndAttemptSaveAction): DnaEpicAction => {
                const state = store$.value;
                const {
                    commonData: {
                        client: { workCurrency },
                        constants
                    }
                } = state;
                const { campaignId, strategy } = action;
                const editorId = getCampaignStrategyEditorId(campaignId);
                const metrikaGoals = getCampaignData(state, campaignId)
                    .availableForKeyGoals;
                const currencyConsts =
                    constants && constants.currencyConstants[workCurrency];

                const validationResult = validateStrategy(
                    workCurrency,
                    metrikaGoals,
                    strategy,
                    currencyConsts
                );

                if (!isStrategyValidationResultPositive(validationResult)) {
                    return of(
                        applyStrategyEditorValidationResult(
                            editorId,
                            validationResult
                        )
                    );
                }

                return [
                    updateCampaignStrategy(
                        campaignId,
                        mapStrategyForCampaignData(strategy)
                    ),
                    closeStrategyEditor(editorId)
                ];
            }
        )
    );
};

export const saveCampaignMinusPhrasesEditorEpic: DnaEpic = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(SAVE_CAMPAIGN_MINUS_PHRASES_EDITOR),
        mergeMap(
            (action: SaveCampaignMinusPhrasesEditor): DnaEpicAction => {
                const { campaignId } = action;
                const state = store$.value;

                const editorId = getCampaignMinusPhrasesEditorId(campaignId);
                const {
                    libraryItems,
                    data: {
                        [editorId]: { minusKeywordsEditor }
                    }
                } = state.minusPhraseLibraryEditor;

                if (state.commonData.features.isMinusWordsLibEnabled) {
                    const offset = 0;
                    const constants = state.commonData.constants;

                    if (!constants) {
                        return EMPTY;
                    }

                    const limit =
                        constants.minusPhraseLibraryConstants
                            .maxLibraryPacksCount;

                    // Заново запросить минус-фразы на случай, если были выбраны удаленные элементы
                    return from(
                        deps.minusPhraseLibraryDataProvider.fetchMinusPhraseLibraryData(
                            offset,
                            limit
                        )
                    ).pipe(
                        mergeMap(
                            (
                                response: FetchMinusPhraseLibraryDataResult
                            ): Action[] => {
                                const updatedLibraryItems = mapFetchResultToLibraryItem(
                                    response.data
                                );

                                return [
                                    updateCampaignMinusPhrases(
                                        campaignId,
                                        mapMinusPhrasesEditorValueToCampaignMinusPhrasesValue(
                                            minusKeywordsEditor,
                                            updatedLibraryItems || []
                                        )
                                    ),
                                    closeMinusPhrasesEditor(editorId)
                                ];
                            }
                        )
                    );
                }

                return from([
                    updateCampaignMinusPhrases(
                        campaignId,
                        mapMinusPhrasesEditorValueToCampaignMinusPhrasesValue(
                            minusKeywordsEditor,
                            libraryItems || []
                        )
                    ),
                    closeMinusPhrasesEditor(editorId)
                ]);
            }
        )
    );
};

export const saveVisitingCardForEditCampaignEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(SAVE_VISITING_CARD_FOR_EDIT_CAMPAIGN),
        switchMap(
            (action: SaveVisitingCardForEditCampaign): Observable<Action> => {
                const { campaignId } = action;
                const editorId = getCampaignEditPageEditorId(campaignId);

                return of(
                    synchronizeVisitingCardForEditCampaigns(campaignId),
                    closeVCardEditor(editorId)
                );
            }
        )
    );
};
